<script setup lang="ts">
import { ArrowRight, Check } from "lucide-vue-next";
import PublicPageLayout from "~/components/public/PublicPageLayout.vue";

const { t } = useI18n();
const localeRoute = useLocaleRoute();

const { canonicalUrl, currentLocale } =
  useLocalizedSeoLinks("/for-professionals");

useSeoMeta({
  title: () => t("public_pages.for_professionals.seo.title"),
  description: () => t("public_pages.for_professionals.seo.description"),
  robots: "index, follow",
  ogTitle: () => t("public_pages.for_professionals.seo.title"),
  ogDescription: () => t("public_pages.for_professionals.seo.description"),
  ogType: "website",
  ogUrl: () => canonicalUrl.value,
  ogLocale: () => currentLocale.value.ogLocale,
  ogSiteName: "Senta.lt",
});

const benefits = ["free", "visible", "direct", "control"] as const;
const steps = ["profile", "services", "clients"] as const;
</script>

<template>
  <PublicPageLayout
    :eyebrow="$t('professionals_cta.label')"
    :title="$t('public_pages.for_professionals.title')"
    :description="$t('public_pages.for_professionals.description')"
  >
    <div class="max-w-[1180px]">
      <section>
        <h2 class="text-3xl font-bold tracking-tight text-app-text">
          {{ $t("public_pages.for_professionals.benefits_title") }}
        </h2>

        <ul class="mt-9 grid gap-x-16 gap-y-8 sm:grid-cols-2">
          <li
            v-for="benefit in benefits"
            :key="benefit"
            class="flex max-w-md gap-3"
          >
            <Check class="mt-1 size-4 shrink-0 text-primary" />
            <div>
              <strong class="text-base text-app-text">
                {{
                  $t(`public_pages.for_professionals.benefits.${benefit}.title`)
                }}
              </strong>
              <p class="mt-1.5 text-sm leading-6 text-app-muted">
                {{
                  $t(
                    `public_pages.for_professionals.benefits.${benefit}.description`,
                  )
                }}
              </p>
            </div>
          </li>
        </ul>
      </section>

      <section
        class="relative mt-20 overflow-hidden rounded-[28px] bg-primary px-7 py-8 text-white sm:mt-24 sm:px-10 sm:py-10 lg:px-12"
      >
        <div
          aria-hidden="true"
          class="pointer-events-none absolute -right-16 -top-24 size-64 rounded-full border border-white/10"
        />
        <div
          aria-hidden="true"
          class="pointer-events-none absolute -bottom-24 right-24 size-48 rounded-full border border-white/10"
        />

        <div
          class="relative flex flex-col gap-8 sm:flex-row sm:items-center sm:justify-between sm:gap-12"
        >
          <div class="max-w-2xl">
            <p
              class="text-xs font-bold uppercase tracking-[0.14em] text-white/65"
            >
              {{ $t("professionals_cta.label") }}
            </p>

            <div class="mt-4 flex items-end gap-3">
              <span class="text-5xl font-bold tracking-tight sm:text-6xl"
                >0 €</span
              >
              <span class="pb-1.5 text-sm font-medium text-white/70">
                {{ $t("public_pages.for_professionals.registration_price") }}
              </span>
            </div>

            <p class="mt-5 max-w-xl text-base leading-7 text-white/80">
              {{ $t("public_pages.for_professionals.cta_description") }}
            </p>
          </div>

          <NuxtLink
            :to="localeRoute('/register') || '/register'"
            class="group inline-flex h-12 shrink-0 items-center justify-center gap-2 self-start rounded-xl bg-white px-6 text-sm font-bold text-primary transition hover:bg-white/90 sm:self-center"
          >
            {{ $t("professionals_cta.actions.register") }}
            <ArrowRight
              class="size-4 transition-transform group-hover:translate-x-0.5"
            />
          </NuxtLink>
        </div>
      </section>

      <section class="mt-20 sm:mt-24">
        <div class="max-w-3xl">
          <h2 class="text-3xl font-bold tracking-tight text-app-text">
            {{ $t("public_pages.for_professionals.steps_title") }}
          </h2>
          <p class="mt-4 max-w-2xl leading-7 text-app-muted">
            {{ $t("public_pages.for_professionals.steps_description") }}
          </p>
        </div>

        <ol class="mt-10 grid gap-10 sm:grid-cols-3 sm:gap-14">
          <li v-for="(step, index) in steps" :key="step" class="max-w-xs">
            <span class="text-xs font-bold tabular-nums text-primary"
              >0{{ index + 1 }}</span
            >
            <h3 class="mt-3 text-lg font-bold text-app-text">
              {{ $t(`public_pages.for_professionals.steps.${step}.title`) }}
            </h3>
            <p class="mt-2 text-sm leading-6 text-app-muted">
              {{
                $t(`public_pages.for_professionals.steps.${step}.description`)
              }}
            </p>
          </li>
        </ol>
      </section>
    </div>
  </PublicPageLayout>
</template>
